
import React, { useEffect } from 'react';
import { TableIcon } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { isValidTableName } from '@/utils/organization/tables';

interface TableSelectorProps {
  availableTables: any[];
  selectedTable: { schema: string, tableName: string } | null;
  onTableSelect: (tableInfo: string) => void;
  getDisplayTableName: (fullTableName: string) => string;
}

const TableSelector: React.FC<TableSelectorProps> = ({
  availableTables,
  selectedTable,
  onTableSelect,
  getDisplayTableName
}) => {

  const validTables = availableTables.filter(table => isValidTableName(table.table_name));

  const handleTableChange = (value: string) => {
    onTableSelect(value);
  };

  // Prioritize csv_table_registry if it exists, but filter out invalid tables
  const sortedTables = [...validTables].sort((a, b) => {
    // Put csv_table_registry at the top
    if (a.table_name === 'csv_table_registry') return -1;
    if (b.table_name === 'csv_table_registry') return 1;
    
    // Then sort alphabetically
    return a.table_name.localeCompare(b.table_name);
  });

  // Format table name by replacing underscores with spaces and capitalizing
  const formatTableName = (tableName: string) => {
    return tableName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get display name: use provided display_name or format the table_name
  const getTableDisplayName = (table: any) => {
    if (table.display_name) {
      return table.display_name;
    }
    return formatTableName(table.table_name);
  };

  return (
    <div data-testid="table-selector" className="flex items-center gap-2">
      <TableIcon size={16} className="text-gray-500" />
      <Select 
        onValueChange={handleTableChange}
        value={selectedTable ? `${selectedTable.schema}.${selectedTable.tableName}` : undefined}
      >
        <SelectTrigger className="w-[250px]">
          <SelectValue placeholder="Select a table" />
        </SelectTrigger>
        <SelectContent className="max-h-[300px] overflow-y-auto">
          {sortedTables.map((table, idx) => {
            const value = `${table.table_schema}.${table.table_name}`;
            // Use display_name if available, otherwise format the table_name
            const displayName = getTableDisplayName(table);
            
            return (
              <SelectItem 
                key={idx} 
                value={value}
              >
                {displayName}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};

export default TableSelector;
